import PropTypes from 'prop-types';
import { isEmpty, isNil } from 'ramda';
import { formatMoneyWithSign } from 'utils/currencyHelper';
import { formatTime } from 'utils/dateHelpers';
import Presenter from 'utils/PropTypesPresenter';
import { upperFirstLetter, humanizeString } from 'utils/helperMethods';

const LOCK_PERIOD_IN_DAYS = 30;

export default new Presenter(
  {
    id: PropTypes.number,
    apr: PropTypes.string,
    buyerId: PropTypes.number,
    buyerFriendlyId: PropTypes.string,
    buyerLastActivity: PropTypes.string,
    creditScore: PropTypes.number,
    canMakeBid: PropTypes.bool,
    cashOutAmount: PropTypes.string,
    city: PropTypes.string,
    downPaymentTarget: PropTypes.string,
    dtiEstimate: PropTypes.string,
    email: PropTypes.string,
    firstTime: PropTypes.bool,
    hadBankrupt: PropTypes.bool,
    hadForeclosure: PropTypes.bool,
    hadMilitaryService: PropTypes.bool,
    incomeAnnualPreTax: PropTypes.string,
    ltvTarget: PropTypes.string,
    monthlyDebt: PropTypes.string,
    mortgageTermYears: PropTypes.number,
    occupancy: PropTypes.string,
    phoneNumber: PropTypes.string,
    preferredTerm: PropTypes.number,
    priceTarget: PropTypes.string,
    propertyType: PropTypes.string,
    purpose: PropTypes.string,
    realtor: PropTypes.string,
    reasonMessage: PropTypes.string,
    requestType: PropTypes.string,
    stage: PropTypes.string,
    state: PropTypes.string,
    stateName: PropTypes.string,
    status: PropTypes.string,
  },
  {
    getLoanAmmount(bidRequest) {
      const downPaymentTarget = this.getDownPaymentTarget(bidRequest);
      const priceTarget = this.getPriceTarget(bidRequest);
      return (priceTarget - downPaymentTarget).toFixed(0);
    },
    getIncomeAnnualPreTax(bidRequest) {
      return Number.parseFloat(this.incomeAnnualPreTax(bidRequest));
    },
    getDownPaymentTarget(bidRequest) {
      return Number.parseFloat(this.downPaymentTarget(bidRequest)).toFixed(0);
    },
    getDtiEstimate(bidRequest) {
      return Number.parseFloat(this.dtiEstimate(bidRequest));
    },
    getCashOutAmount(bidRequest) {
      const cashOutAmount = this.cashOutAmount(bidRequest);
      if (isNil(cashOutAmount)) return 0;
      return Number.parseFloat(this.cashOutAmount(bidRequest));
    },
    getMonthlyDebt(bidRequest) {
      const monthlyDebt = this.monthlyDebt(bidRequest);
      if (isNil(monthlyDebt)) return 0;
      return Number.parseFloat(this.monthlyDebt(bidRequest));
    },
    getPriceTarget(bidRequest) {
      return Number.parseFloat(this.priceTarget(bidRequest)).toFixed(0);
    },
    readalbeLoanAmmount(bidRequest) {
      const loanAmount = this.getLoanAmmount(bidRequest);
      return formatMoneyWithSign(loanAmount, 'USD');
    },
    getLtv(bidRequest) {
      return Number.parseFloat(this.ltvTarget(bidRequest));
    },
    readableLtv(bidRequest) {
      const ltv = this.getLtv(bidRequest) * 100;
      if (Number.isInteger(ltv)) return `${ltv}%`;
      return `${ltv.toFixed(2)}%`;
    },
    readablePriceTarget(bidRequest) {
      const priceTarget = Number.parseFloat(this.priceTarget(bidRequest)).toFixed(0);
      return formatMoneyWithSign(priceTarget, 'USD');
    },
    readalbeCashOutAmmount(bidRequest) {
      const cashOutAmount = this.cashOutAmount(bidRequest);
      return formatMoneyWithSign(cashOutAmount, 'USD');
    },
    buyerFullName(bidRequest) {
      const [firstName, lastName] = this.buyerFriendlyId(bidRequest).split('_');
      return `${upperFirstLetter(firstName)} ${upperFirstLetter(lastName)}`;
    },
    location(bidRequest) {
      const city = this.city(bidRequest);
      const stateName = this.stateName(bidRequest);
      return `${city}, ${stateName}`;
    },
    humanFirstTime(bidRequest) {
      return this.firstTime(bidRequest) ? 'Yes' : 'No';
    },
    humanHasMilitaryService(bidRequest) {
      return this.hadMilitaryService(bidRequest) ? 'Yes' : 'No';
    },
    humanFoundHome(bidRequest) {
      return this.stage(bidRequest) === 'approved' ? 'Yes' : 'No';
    },
    isPurchase(bidRequest) {
      return this.purpose(bidRequest) === 'purchase';
    },
    formatRequestDate(bidRequest) {
      return formatTime(this.buyerLastActivity(bidRequest));
    },
    lockPeriodInDays() {
      return `${LOCK_PERIOD_IN_DAYS} days`;
    },
    creditScoreEstimate(bidRequest) {
      const creditScore = this.creditScore(bidRequest);
      switch (true) {
        case creditScore < 300:
          return '< 300';
        case creditScore < 620:
          return '580-619';
        case creditScore < 680:
          return '620-679';
        case creditScore < 740:
          return '680-739';
        case creditScore < 850:
          return '740+';
        default:
          return '> 850';
      }
    },
    readableAnnualPreTaxIncome(bidRequest) {
      const incomeAnnualPreTax = this.incomeAnnualPreTax(bidRequest);
      return formatMoneyWithSign(incomeAnnualPreTax, 'USD');
    },
    readableMonthlyDebtPayment(bidRequest) {
      const monthlyDebt = this.monthlyDebt(bidRequest);
      return formatMoneyWithSign(monthlyDebt, 'USD');
    },
    readableDtiEstimate(bidRequest) {
      const dtiEstimate = Number.parseFloat(this.dtiEstimate(bidRequest));
      return `${dtiEstimate.toFixed(2)}%`;
    },
    formatOccupancy(bidRequest) {
      if (isEmpty(this.occupancy(bidRequest))) return '';
      return humanizeString(this.occupancy(bidRequest));
    },
    formatPurpose(bidRequest) {
      if (isEmpty(this.purpose(bidRequest))) return '';
      return humanizeString(this.purpose(bidRequest));
    },
    formatPropertyType(bidRequest) {
      if (isEmpty(this.propertyType(bidRequest))) return '';
      return humanizeString(this.propertyType(bidRequest));
    },
    humanHadBankrupt(bidRequest) {
      return this.hadBankrupt(bidRequest) ? 'Yes' : 'No';
    },
    humanHadForeclosure(bidRequest) {
      return this.hadForeclosure(bidRequest) ? 'Yes' : 'No';
    },
  },
);



// WEBPACK FOOTER //
// ./src/presenters/BidRequestPresenter.js